import React, { useRef, useEffect } from "react";

const ChatInput = ({ value, onChange, onSend, loading, editingMessage, onCancelEdit }) => {
  const textareaRef = useRef(null);

  // Auto-grow textarea — single line at rest, expands up to 140px
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 140) + "px";
  }, [value]);

  // Focus textarea when edit mode activates
  useEffect(() => {
    if (editingMessage) textareaRef.current?.focus();
  }, [editingMessage]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!loading && value.trim()) onSend();
    }
  };

  return (
    <div className="flex-shrink-0 bg-white border-t border-gray-100 shadow-[0_-1px_6px_rgba(0,0,0,0.04)]">
      <div className="max-w-2xl mx-auto w-full px-4 md:px-6 py-4">

        {/* Edit mode banner */}
        {editingMessage && (
          <div className="flex items-center gap-2 mb-3 text-xs text-teal-700 bg-teal-50 border border-teal-100 px-3 py-2 rounded-xl">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            <span className="font-medium">Editing message</span>
            <button
              onClick={onCancelEdit}
              className="ml-auto text-gray-400 hover:text-gray-700 font-semibold transition-colors"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Input row — items-center keeps send button vertically centred at all heights */}
        <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 shadow-sm focus-within:border-teal-400 focus-within:ring-2 focus-within:ring-teal-100 transition-all duration-150">
          <textarea
            ref={textareaRef}
            rows={1}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            disabled={loading}
            className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 outline-none resize-none leading-6 disabled:opacity-50"
            style={{ minHeight: "24px", maxHeight: "140px" }}
          />

          <button
            onClick={onSend}
            disabled={loading || !value.trim()}
            className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center bg-teal-600 hover:bg-teal-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white transition-colors duration-150 shadow-sm"
            title="Send (Enter)"
          >
            {loading ? (
              <svg className="animate-spin" width="15" height="15" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="19" x2="12" y2="5" />
                <polyline points="5 12 12 5 19 12" />
              </svg>
            )}
          </button>
        </div>

        <p className="text-center text-[10px] text-gray-400 mt-2 select-none">
          Press <kbd className="bg-gray-100 px-1 py-0.5 rounded text-gray-500">Enter</kbd> to send &nbsp;·&nbsp;
          <kbd className="bg-gray-100 px-1 py-0.5 rounded text-gray-500">Shift + Enter</kbd> for new line
        </p>
      </div>
    </div>
  );
};

export default ChatInput;
